/**
 * feat-241 — the crew curriculum passport (Concept C, AC18).
 *
 * The matrix's row drill-down: completed tracks in their completed art, the
 * in-progress one with a bar and its next lesson title, locked ones dimmed with
 * what they are waiting on.
 *
 * A LINKABLE ROUTE (P5), not a dialog — shareable with a district manager, the
 * back button works, and it is testable without driving a dialog open.
 *
 * IT PERFORMS NO SECOND FETCH (P12). It reads the response the shared provider
 * already holds, so there is exactly one endpoint and one evaluator path; two
 * would be two places for "is this track locked" to be answered differently.
 *
 * TRACK-LEVEL ONLY (P6). Lesson-level detail on a manager surface is a step
 * toward the lesson-level fence in the program's Non-goals.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import { Check, ChevronLeft, Lock } from 'lucide-react';
import {
  CELL_BLOCKED_BY_FULL,
  CELL_FRACTION,
  CELL_LABEL,
  COVERAGE_ERROR_BODY,
  COVERAGE_ERROR_RETRY,
  COVERAGE_ERROR_TITLE,
  COVERAGE_LOADING,
  IN_PROGRESS_INK,
  PASSPORT_BACK,
  PASSPORT_GROUP_LABEL,
  PASSPORT_LESSONS,
  PASSPORT_NEXT_LESSON_PREFIX,
  PASSPORT_NOT_FOUND_BODY,
  PASSPORT_NOT_FOUND_TITLE,
  TRACKS_COMPLETE,
  type CellStatus,
} from '../../constants/curriculumCoverage';
import type {
  CoverageCell,
  CoverageTrack,
} from '../../services/curriculumCoverageService';
import {
  applicableTrackCount,
  cellApplies,
  cellLessonsTotal,
  notApplicableTrackIds,
} from './curriculumCoverage/coverageApplies';
import { useCurriculumCoverage } from './curriculumCoverage/CurriculumCoverageProvider';
import { PassportTrackArt } from './curriculumCoverage/PassportTrackArt';
import { resolveBlockerTitles } from './curriculumCoverage/coverageBlockers';

const COVERAGE_PATH = '/restaurant/curriculum-coverage';

/**
 * Card order: grouped by state, in AC15's order (design R4). Within a group, the
 * response's own `tracks` order — the matrix's left-to-right column order.
 *
 * Reusing the already-pinned order avoids inventing a second one. Pure
 * `sort_order` with no grouping would keep a 1:1 correspondence with the matrix
 * row, but it buries "what has this person achieved", which is the passport's
 * whole job.
 */
const GROUP_ORDER: CellStatus[] = ['completed', 'in_progress', 'available', 'locked'];

const TrackCard: React.FC<{
  track: CoverageTrack;
  cell: CoverageCell;
  tracks: readonly CoverageTrack[];
  notApplicable: ReadonlySet<string>;
}> = ({ track, cell, tracks, notApplicable }) => {
  const status = cell.status;
  // The dim is on the ARTWORK only — never the card, which would take the ink
  // down with it.
  const dim = status === 'locked' ? 'grayscale opacity-40' : '';
  // feat-302 F1: this person's own lesson count when the backend sends one.
  const lessonsTotal = cellLessonsTotal(cell, track);
  const pct =
    lessonsTotal > 0
      ? Math.min(100, Math.round((cell.lessons_complete / lessonsTotal) * 100))
      : 0;
  const blockers =
    status === 'locked' ? resolveBlockerTitles(cell.blocking_track_ids, tracks, notApplicable) : [];

  return (
    <div className="overflow-hidden rounded-2xl border border-softBorder bg-white">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-cream">
        <div className={`h-full w-full ${dim}`}>
          <PassportTrackArt track={track} status={status} />
        </div>
        {status === 'locked' ? (
          <span className="absolute right-2 top-2 flex items-center justify-center rounded-full bg-white p-1.5 text-inkMuted shadow-sm">
            <Lock aria-hidden size={12} strokeWidth={3} />
          </span>
        ) : null}
      </div>

      <div className="px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-bold text-ink" title={track.title}>
            {track.title}
          </span>

          {status === 'completed' ? (
            <span className="flex shrink-0 items-center gap-1 rounded-lg bg-success-light px-2 py-1 text-[11px] font-bold text-success-on">
              <Check aria-hidden size={11} strokeWidth={3.5} />
              {CELL_LABEL.completed}
            </span>
          ) : null}

          {status === 'in_progress' ? (
            <span
              className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold ${IN_PROGRESS_INK.chipFill} ${IN_PROGRESS_INK.text}`}
            >
              <span className="sr-only">{CELL_LABEL.in_progress} — </span>
              {CELL_FRACTION(cell.lessons_complete, lessonsTotal)}
            </span>
          ) : null}

          {status === 'available' ? (
            <span className="shrink-0 rounded-lg bg-gray-100 px-2 py-1 text-[11px] font-bold text-inkMuted">
              {CELL_LABEL.available}
            </span>
          ) : null}

          {status === 'locked' ? (
            <span className="flex shrink-0 items-center gap-1 rounded-lg border border-dashed border-inkMuted/30 px-2 py-1 text-[11px] font-bold text-inkMuted">
              <Lock aria-hidden size={11} strokeWidth={3} />
              {CELL_LABEL.locked}
            </span>
          ) : null}
        </div>

        {status === 'in_progress' ? (
          <>
            <div
              className={`mt-1.5 h-[5px] w-full overflow-hidden rounded-full ${IN_PROGRESS_INK.barTrack}`}
              role="presentation"
            >
              <div
                className={`h-full rounded-full ${IN_PROGRESS_INK.barFill}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {cell.next_lesson_title ? (
              <div
                className={`mt-1.5 truncate text-[11px] ${IN_PROGRESS_INK.text}`}
                title={cell.next_lesson_title}
              >
                <span className="font-bold">{PASSPORT_NEXT_LESSON_PREFIX}</span>{' '}
                {cell.next_lesson_title}
              </div>
            ) : null}
          </>
        ) : null}

        {status === 'completed' || status === 'available' ? (
          <div className="mt-1 text-[11px] text-inkMuted">
            {PASSPORT_LESSONS(cell.lessons_complete, lessonsTotal)}
          </div>
        ) : null}

        {/* The matrix truncates to `Needs Barista +2` because the column is
            ~112px; here there is room, so the full list renders UNCLIPPED. A
            blocking id with no matching track is dropped rather than printed as
            a dangling "Needs ". */}
        {status === 'locked' && blockers.length > 0 ? (
          <div className="mt-1 text-[11px] text-inkMuted">{CELL_BLOCKED_BY_FULL(blockers)}</div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * The site chrome, in ONE place because this page has four return branches
 * (loading, error, not-found, and the passport itself) and every one of them
 * must carry it.
 *
 * THIS CODEBASE HAS NO SHARED LAYOUT WRAPPER — every page renders
 * <ScorecardNavigation /> itself (TrainingProgressPage:348, LiveServers,
 * PilotDashboardPage), and these two pages never did, which dropped the user out
 * of the site's navigation entirely — tenant selector included, and on a
 * multi-tenant surface that is not optional. ScorecardNavigation renders
 * TenantSelector itself, so this one component delivers the whole framework.
 *
 * The emitted DOM is TrainingProgressPage's verbatim: `min-h-screen bg-cream`
 * wrapper, the nav, then `mx-auto max-w-7xl px-4 md:px-8 py-8` on the <main>.
 * Factored into a local component rather than pasted four times — same output,
 * one place to keep it identical to the siblings.
 */
const PassportShell: React.FC<{
  children: React.ReactNode;
  /** Loading branch only — announces the region (design F4). */
  busy?: boolean;
}> = ({ children, busy }) => (
  <div className="min-h-screen bg-cream">
    <ScorecardNavigation />
    <main
      className="mx-auto max-w-7xl px-4 md:px-8 py-8"
      aria-busy={busy || undefined}
      aria-live={busy ? 'polite' : undefined}
    >
      {children}
    </main>
  </div>
);

const CurriculumPassportPage: React.FC = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const { data, loading, error, forbidden, retry } = useCurriculumCoverage();

  const member = data?.crew.find((c) => c.server_id === serverId);

  const backLink = (
    <Link
      to={COVERAGE_PATH}
      className="inline-flex items-center gap-1.5 text-[12px] font-bold text-coral hover:underline"
    >
      <ChevronLeft aria-hidden size={13} strokeWidth={3} />
      {PASSPORT_BACK}
    </Link>
  );

  // design F4 (a11y half) — announced, not just rendered. Same treatment as the
  // matrix; the designed skeleton is out of scope on both surfaces.
  if (loading) {
    return (
      <PassportShell busy>
        <span className="sr-only">{COVERAGE_LOADING}</span>
        <p className="text-sm text-inkMuted" aria-hidden>
          {COVERAGE_LOADING}
        </p>
      </PassportShell>
    );
  }

  // design F5 — same sentence and same way out as the matrix. A manager who
  // shared a passport URL during the rollout window lands HERE, not on the
  // matrix, so the guidance cannot live on one surface only.
  if (error || forbidden) {
    return (
      <PassportShell>
        {backLink}
        <div
          role="alert"
          className="mt-4 rounded-2xl border border-softBorder bg-white px-6 py-12 text-center"
        >
          <h2 className="font-display text-lg font-semibold text-ink">{COVERAGE_ERROR_TITLE}</h2>
          <p className="mx-auto mt-1.5 max-w-[46ch] text-sm text-inkMuted">{COVERAGE_ERROR_BODY}</p>
          <button
            type="button"
            onClick={retry}
            className="mt-5 rounded-lg bg-coral px-4 py-2 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-coral/40"
          >
            {COVERAGE_ERROR_RETRY}
          </button>
        </div>
      </PassportShell>
    );
  }

  // design R6 — ONE message, whether they were removed or are at a location this
  // manager cannot see. Distinguishing the two would turn a shareable URL (P5)
  // into a tenant-membership oracle, and this feature already carries an RBAC
  // risky-change flag.
  if (!member || !data) {
    return (
      <PassportShell>
        {backLink}
        <div className="mt-4 rounded-2xl border border-softBorder bg-white p-6">
          <h2 className="text-sm font-bold text-ink">{PASSPORT_NOT_FOUND_TITLE}</h2>
          <p className="mt-1 text-sm text-inkMuted">{PASSPORT_NOT_FOUND_BODY}</p>
        </div>
      </PassportShell>
    );
  }

  const { tracks } = data;
  // feat-302: the denominator is the tracks that APPLY to this person.
  const applicable = applicableTrackCount(member);
  const notApplicable = notApplicableTrackIds(member);
  const pctComplete = applicable > 0 ? Math.round((member.tracks_complete / applicable) * 100) : 0;

  // `cells` is dense and track-ordered by contract, so pairing by index preserves
  // the response's own track order within each group. feat-302: a track that
  // does not apply to this person gets no card — the passport is their curriculum.
  const paired = tracks
    .map((track, i) => ({ track, cell: member.cells[i] }))
    .filter((p): p is { track: CoverageTrack; cell: CoverageCell } => Boolean(p.cell))
    .filter((p) => cellApplies(p.cell));

  return (
    <PassportShell>
      <div className="rounded-2xl border border-softBorder bg-white">
        <div className="border-b border-softBorder px-6 py-5">
          {backLink}
          <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
            <div>
              {/* SECURITY HIGH-1 — masked from Sentry Session Replay, same reason
                  and same mechanism as the matrix's sticky column. Neither of
                  these carries a `title` attribute, so the text mask is the whole
                  fix here; see CoverageMatrix.tsx for why an attribute would not
                  have been covered by it. The tracks-complete summary beside them
                  is deliberately NOT masked. */}
              <h1 data-sentry-mask className="font-display text-2xl font-semibold text-ink">
                {member.name}
              </h1>
              <p data-sentry-mask className="mt-0.5 h-5 text-sm text-inkMuted">
                {member.location_name}
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-semibold text-ink">
                {TRACKS_COMPLETE(member.tracks_complete, applicable)}
              </div>
              {/* The ONE place success.DEFAULT appears as a fill on these two
                  surfaces. It is a GRAPHIC on a cream-dark track, never ink on a
                  light surface (2.927:1 on white). */}
              <div className="mt-1.5 h-[6px] w-[180px] overflow-hidden rounded-full bg-cream-dark">
                <div className="h-full rounded-full bg-success" style={{ width: `${pctComplete}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8 px-6 py-6">
          {GROUP_ORDER.map((status) => {
            const items = paired.filter((p) => p.cell.status === status);
            if (items.length === 0) return null;
            return (
              <div key={status} data-testid={`passport-group-${status}`}>
                <h2 className="mb-3 text-[11px] font-bold uppercase tracking-widest text-inkMuted">
                  {PASSPORT_GROUP_LABEL[status]} <span className="text-ink">· {items.length}</span>
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {items.map(({ track, cell }) => (
                    <TrackCard
                      key={track.id}
                      track={track}
                      cell={cell}
                      tracks={tracks}
                      notApplicable={notApplicable}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PassportShell>
  );
};

export default CurriculumPassportPage;
